import React, { useState, useRef, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { useSubscription } from "@/hooks/useSubscription";
import { useProfile } from "@/hooks/useProfile";
import rehypeSanitize from "rehype-sanitize";
import { useAuth } from "@/hooks/useAuth";
import {
  calculIR,
  calculCotisationsTotal_micro,
  calculBeneficeMicro,
  calculURSSAF_bnc,
} from "@/utils/fiscal";

const C = {
  navy: "#1B3A5C", teal: "#2A9D8F", bg: "#F4F8FC", white: "#FFFFFF",
  border: "#D6E4F0", muted: "#6B85A3", lightTeal: "#E8F5F4", danger: "#E63946",
};

const MAX_MESSAGES = 20;
// Quota messages gratuits — doit correspondre à IA_FREE_QUOTA dans assistant-fiscal/index.ts
const IA_FREE_QUOTA = 5;

// ─── Messages rotatifs pendant le chargement IA (toutes les 20 s) ────────────
const LOADING_MESSAGES = [
  "Analyse de vos données fiscales en cours...",
  "Vérification des taux URSSAF 2026...",
  "Calcul de l'optimisation fiscale...",
  "Croisement avec le barème IR 2026...",
  "Préparation de votre conseil personnalisé...",
  "Vérification des seuils Micro-BNC et TVA...",
  "Finalisation de votre diagnostic fiscal...",
];

interface Message {
  role: "user" | "assistant" | "error";
  content: string;
}

// ─── Quick Reply Prompts (Hormozi) ────────────────────────────────────────────
function buildQuickPrompts(profile: any) {
  const targetCa = Number(profile?.target_monthly_ca ?? 4500);
  const charges = Number(profile?.estimated_monthly_charges ?? 800);
  const tarif = Number(profile?.average_session_rate ?? 70);
  const caAnnuel = targetCa * 12;
  const ratio = targetCa > 0 ? (charges / targetCa) * 100 : 0;
  const isMicro = profile?.statut_fiscal !== "bnc-classique";

  if (isMicro) {
    // ── Micro-BNC : questions orientées transition / optimisation ──
    return [
      {
        label: "💰 Suis-je en train de trop payer ?",
        prompt: ratio > 34
          ? `Je suis en Micro-BNC. Mes données : CA mensuel ${targetCa}€, charges mensuelles ${charges}€. Mon ratio charges/CA est de ${ratio.toFixed(1)}%, ce qui dépasse l'abattement forfaitaire de 34% du Micro-BNC. Calcule combien je perds fiscalement en restant en Micro-BNC par rapport au BNC Classique. Donne-moi le chiffre exact de mon surcoût annuel.`
          : `Je suis en Micro-BNC. Mes données : CA mensuel ${targetCa}€, charges mensuelles ${charges}€. Mon ratio charges/CA est de ${ratio.toFixed(1)}%. Dis-moi si je suis dans la bonne situation fiscale ou s'il vaudrait mieux passer au BNC Classique. Explique avec des chiffres précis.`,
      },
      {
        label: "📊 Quand passer au BNC Classique ?",
        prompt: `Je suis actuellement en Micro-BNC. CA mensuel : ${targetCa}€ (${caAnnuel}€/an), charges mensuelles : ${charges}€. Explique-moi à partir de quel niveau de charges ou de CA il devient rentable de passer au BNC Classique. Quel est le point de bascule exact pour mon profil ?`,
      },
      {
        label: "🚀 Mon seuil Micro-BNC 2026",
        prompt: `Je suis en Micro-BNC. Mon CA mensuel est de ${targetCa}€, soit ${caAnnuel}€/an. Le seuil Micro-BNC 2026-2028 est de 83 600€. À quel mois de l'année risqué-je de le dépasser si mon activité progresse ? Mon tarif par séance est de ${tarif}€. Que se passe-t-il si je dépasse ce seuil et que dois-je anticiper ?`,
      },
    ];
  } else {
    // ── BNC Classique : questions orientées optimisation / charges / 2035 ──
    return [
      {
        label: "📋 Optimiser mes charges déductibles",
        prompt: `Je suis en BNC Classique. CA mensuel : ${targetCa}€, charges mensuelles déclarées : ${charges}€. Fais-moi un audit de toutes les charges déductibles que je peux passer en tant que psychologue : loyer cabinet, supervision, formations, matériel, téléphone, voiture, mutuelle Madelin... Qu'est-ce que j'oublie peut-être ?`,
      },
      {
        label: "🎓 Simuler l'achat d'une formation",
        prompt: `Je suis en BNC Classique. Je veux acheter une formation professionnelle. Demande-moi son prix, puis calcule son coût réel après déduction : sur une formation à X€, combien récupéré-je via la réduction de mes cotisations CIPAV et de mon impôt sur le revenu ? Mon CA mensuel est de ${targetCa}€ et mes charges actuelles sont de ${charges}€.`,
      },
      {
        label: "📊 Comprendre ma déclaration 2035",
        prompt: `Je suis en BNC Classique, je dois remplir ma déclaration 2035. CA mensuel ${targetCa}€ (${caAnnuel}€/an), charges mensuelles ${charges}€. Explique-moi les principales lignes de la 2035 à remplir en tant que psychologue : recettes, charges déductibles, cotisations CIPAV, bénéfice imposable. Quelles sont les erreurs fréquentes à éviter ?`,
      },
    ];
  }
}

// ─── Teaser Paywall ──────────────────────────────────────────────────────────
function TeaserPaywall() {
  const navigate = useNavigate();
  return (
    <div
      className="mt-4 rounded-2xl p-6 text-center"
      style={{ backgroundColor: C.navy, color: C.white }}
    >
      <span className="text-3xl block mb-3">✨</span>
      <h3 className="text-base font-bold mb-2">
        Vous avez atteint votre limite de {IA_FREE_QUOTA} conseils gratuits
      </h3>
      <p className="text-sm mb-4" style={{ color: "rgba(255,255,255,0.75)" }}>
        Passez au Plan Pro pour un accès illimité et des analyses plus poussées.
      </p>
      <button
        onClick={() => navigate("/pricing")}
        className="px-6 py-2.5 rounded-xl text-sm font-bold transition-opacity hover:opacity-90"
        style={{ backgroundColor: C.teal, color: C.white }}
      >
        Passer au Plan Pro →
      </button>
    </div>
  );
}

// ─── Trial Badge ─────────────────────────────────────────────────────────────
function TrialBadge({ remaining }: { remaining: number }) {
  const label = remaining === 1
    ? "⚠️ 1 conseil gratuit restant"
    : `🎁 ${remaining} conseils gratuits restants`;
  return (
    <span
      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold"
      style={{ backgroundColor: C.lightTeal, color: C.teal, border: `1px solid ${C.teal}33` }}
    >
      {label}
    </span>
  );
}

// ─── Input sanitization ──────────────────────────────────────────────────────
function sanitizeInput(input: string): string {
  return input
    .replace(/<[^>]*>/g, "")          // Strip HTML tags
    .replace(/javascript:/gi, "")     // Remove JS protocol
    .replace(/on\w+\s*=/gi, "")       // Remove event handlers
    .trim()
    .slice(0, 2000);                  // Max length
}

// ─── Friendly error messages ─────────────────────────────────────────────────
function friendlyError(status: number, rawError?: string): string {
  if (status === 401 || status === 403) return "Session expirée — veuillez vous reconnecter.";
  if (status >= 500) return "L'assistant rencontre une forte affluence, réessayez dans un instant.";
  if (status === 429) return "Trop de requêtes — veuillez patienter quelques secondes.";
  return rawError || "Une erreur est survenue. Réessayez dans un instant.";
}

// ─── Assistant helper (appel direct vers le projet Supabase réel) ─────────────
const ASSISTANT_FN_URL =
  "https://aaqmpjzqelizknxgontd.supabase.co/functions/v1/assistant-fiscal";

async function callAssistant({
  chatInput,
  onResult,
  onError,
}: {
  chatInput: string;
  onResult: (text: string) => void;
  onError: (msg: string) => void;
}) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token ?? "";

    if (!token) {
      onError("Session expirée — veuillez vous reconnecter.");
      return;
    }

    const sanitized = sanitizeInput(chatInput);
    if (!sanitized) {
      onError("Veuillez saisir une question valide.");
      return;
    }

    const res = await fetch(ASSISTANT_FN_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ chatInput: sanitized, sessionId: session?.user?.id || "guest" }),
    });

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      onError(friendlyError(res.status, errBody?.error));
      return;
    }

    const data = await res.json();
    const text =
      data?.output ||
      data?.text ||
      data?.response ||
      data?.message ||
      (typeof data === "string" ? data : JSON.stringify(data));

    onResult(text);
  } catch {
    onError("L'assistant rencontre une forte affluence, réessayez dans un instant.");
  }
}

export default function AssistantChat() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStepIdx, setLoadingStepIdx] = useState(0);
  const [userMsgCount, setUserMsgCount] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useProfile();
  const { plan, isTrial, iaTrialUsed, refetch, loading: subLoading } = useSubscription();
  const isPro = plan === "pro";
  const isEssentiel = !isPro;

  // Quota : bloqué dès que ia_trial_used (source DB) atteint le seuil.
  // La vérification serveur dans assistant-fiscal/index.ts est la source de vérité réelle.
  const iaBlocked = !subLoading && isEssentiel && iaTrialUsed >= IA_FREE_QUOTA;
  const iaRemaining = Math.max(0, IA_FREE_QUOTA - (isEssentiel ? iaTrialUsed : 0));
  const iaTrialAvailable = !subLoading && isEssentiel && iaRemaining > 0;

  const quickPrompts = useMemo(() => buildQuickPrompts(profile), [profile]);

  // Welcome message — calcul fiscal correct avec IR inclus
  const welcomeMessage = useMemo(() => {
    const stored = localStorage.getItem("psygestion_onboarding");
    const fallback = stored ? JSON.parse(stored) : {};
    const targetCa   = Number((profile as any)?.target_monthly_ca          ?? fallback.target_monthly_ca          ?? 0);
    const estCharges = Number((profile as any)?.estimated_monthly_charges   ?? fallback.estimated_monthly_charges  ?? 0);
    const isCurrentlyMicro = (profile as any)?.statut_fiscal !== "bnc-classique";

    if (targetCa <= 0) return null;

    const caAn      = targetCa * 12;
    const chargesAn = estCharges * 12;

    // Micro-BNC : les charges réelles ne sont PAS déductibles
    // Seul l'abattement forfaitaire 34% s'applique sur la base IR
    const netMicro = Math.max(0,
      caAn - calculCotisationsTotal_micro(caAn) - calculIR(calculBeneficeMicro(caAn))
    );

    // BNC Réel : charges déductibles + cotisations ~40% du bénéfice + IR
    const benefice    = Math.max(0, caAn - chargesAn);
    const cipavReel   = calculURSSAF_bnc(benefice);
    const irBaseReel  = Math.max(0, benefice - cipavReel);
    const netReel     = Math.max(0, irBaseReel - calculIR(irBaseReel));

    const reelEstMeilleur = netReel > netMicro;
    const economie        = Math.abs(netReel - netMicro);
    const regimeOptimal   = reelEstMeilleur ? "BNC Classique" : "Micro-BNC";
    const alreadyOptimal  = reelEstMeilleur ? !isCurrentlyMicro : isCurrentlyMicro;

    // Message selon situation — sans montant précis (le taux CIPAV BNC Classique
    // varie selon le bénéfice, une estimation à 40% flat surreprésente l'écart)
    if (economie < 500) {
      return `Votre situation est **équilibrée** entre Micro-BNC et BNC Classique. Je suis votre consultant fiscal 24h/24 — demandez-moi de calculer votre situation exacte ou posez une question sur vos déductions.`;
    }

    if (alreadyOptimal) {
      return `Vous êtes au **${regimeOptimal}** ✅ — probablement le régime le plus avantageux pour votre profil. Je suis votre consultant fiscal 24h/24. Voulez-vous que je vérifie vos charges déductibles ou la réforme 2026 ?`;
    }

    return `Votre profil suggère que le **${regimeOptimal}** pourrait être plus avantageux pour vous. Je suis votre consultant fiscal 24h/24 — demandez-moi de **calculer votre économie exacte** selon votre situation réelle.`;
  }, [profile]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  // ─── Barre de progression simulée + messages rotatifs ─────────────────────
  // Progression logarithmique : rapide au début, lente à la fin, plafond à 92 %
  // Objectif : refléter honnêtement une attente d'environ 60 secondes
  useEffect(() => {
    if (!loading) {
      setLoadingProgress(0);
      setLoadingStepIdx(0);
      return;
    }
    const start = Date.now();
    const tick = setInterval(() => {
      const elapsed = (Date.now() - start) / 1000; // secondes écoulées
      const progress = Math.min(
        92,
        Math.round(
          elapsed < 12  ? elapsed * 3.5          // 0 → 42 % en 12 s
          : elapsed < 35 ? 42 + (elapsed - 12) * 2  // 42 → 88 % en 23 s
          : 88 + (elapsed - 35) * 0.15            // 88 → 92 % très lentement
        )
      );
      setLoadingProgress(progress);
      // Rotation : nouveau message toutes les 20 secondes
      setLoadingStepIdx(Math.floor(elapsed / 20) % LOADING_MESSAGES.length);
    }, 500);
    return () => clearInterval(tick);
  }, [loading]);

  // ─── Alerte avant fermeture de l'onglet ───────────────────────────────────
  // Empêche l'utilisateur de perdre sa consultation en cours
  useEffect(() => {
    if (!loading) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Chrome requiert que returnValue soit défini (même vide)
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [loading]);

  const sendMessage = async (text: string) => {
    const msg = text.trim();
    if (!msg || loading) return;
    if (userMsgCount >= MAX_MESSAGES) return;
    // Garde côté client (la vérification serveur dans assistant-fiscal est la vraie barrière)
    if (iaBlocked) return;

    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (!session || sessionError) { navigate("/auth?mode=login"); return; }

    const newCount = userMsgCount + 1;
    setUserMsgCount(newCount);
    setMessages(prev => [...prev, { role: "user", content: msg }]);
    setInput("");
    setLoading(true);

    try {
      await callAssistant({
        chatInput: msg,
        onResult: (text) => {
          setMessages(prev => [...prev, { role: "assistant" as const, content: text }]);
          // Resync ia_trial_used depuis la DB — l'Edge Function a incrémenté côté serveur
          refetch();
        },
        onError: (errMsg) => {
          setMessages(prev => [...prev, { role: "error", content: errMsg }]);
          // Resync au cas où le 403 quota_exceeded est arrivé via l'Edge Function
          refetch();
        },
      });
    } catch {
      setMessages(prev => [...prev, { role: "error", content: "L'assistant est momentanément indisponible — réessayez dans quelques instants." }]);
    } finally {
      setLoading(false);
    }
  };

  const limitReached = userMsgCount >= MAX_MESSAGES;

  return (
    <div className="max-w-2xl mx-auto flex flex-col px-2 sm:px-0" style={{ height: "calc(100dvh - 180px)", minHeight: 400 }}>
      <style>{`
        @keyframes ia-shimmer {
          0%   { transform: translateX(-150%); }
          100% { transform: translateX(350%); }
        }
        .ia-shimmer { animation: ia-shimmer 1.8s ease-in-out infinite; }
      `}</style>

      {/* Header compact */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div>
          <h2 className="text-base font-bold" style={{ color: C.navy }}>Consultant fiscal IA 🧠</h2>
          <p className="text-xs" style={{ color: C.muted }}>Réponses personnalisées à votre situation 2026</p>
        </div>
        {iaTrialAvailable && iaRemaining < IA_FREE_QUOTA && (
          <TrialBadge remaining={iaRemaining} />
        )}
      </div>

      {/* Zone de messages — flex-1, scroll uniquement quand nécessaire */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto rounded-2xl p-4 space-y-4"
        style={{ backgroundColor: C.bg, border: `1px solid ${C.border}` }}
      >
        {/* État vide */}
        {messages.length === 0 && !loading && !iaBlocked && (
          <div className="flex flex-col gap-4 h-full justify-center">

            {/* Message de bienvenue comme bulle assistant */}
            <div className="flex justify-start gap-2">
              <span className="text-lg shrink-0 mt-0.5">🧠</span>
              <div className="rounded-2xl rounded-tl-sm px-4 py-3 text-sm leading-relaxed" style={{ backgroundColor: C.white, color: C.navy, border: `1px solid ${C.border}`, maxWidth: "90%" }}>
                {welcomeMessage ? (
                  <div className="prose prose-sm max-w-none [&_p]:m-0">
                    <ReactMarkdown rehypePlugins={[rehypeSanitize]}>{welcomeMessage}</ReactMarkdown>
                  </div>
                ) : (
                  "Bonjour ! Je suis votre consultant fiscal 24h/24. Posez-moi une question ou choisissez une analyse rapide ci-dessous."
                )}
              </div>
            </div>

            {/* Quick prompts — grille 1 col sur mobile, lisibles */}
            <div className="grid grid-cols-1 gap-2 px-1">
              {quickPrompts.map((qp, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(qp.prompt)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-left transition-all duration-150 hover:scale-[1.01] active:scale-[0.99]"
                  style={{
                    backgroundColor: C.white,
                    color: C.navy,
                    border: `1px solid ${C.border}`,
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLButtonElement).style.borderColor = C.teal;
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = C.lightTeal;
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLButtonElement).style.borderColor = C.border;
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = C.white;
                  }}
                >
                  <span className="text-base shrink-0">{qp.label.split(" ")[0]}</span>
                  <span>{qp.label.slice(qp.label.indexOf(" ") + 1)}</span>
                  <span className="ml-auto shrink-0 text-xs" style={{ color: C.muted }}>→</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role !== "user" && (
              <span className="text-lg shrink-0 mt-0.5">{m.role === "error" ? "❌" : "🧠"}</span>
            )}
            <div
              className="max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed"
              style={
                m.role === "user"
                  ? { backgroundColor: C.teal, color: C.white, borderRadius: "18px 18px 4px 18px" }
                  : m.role === "error"
                  ? { backgroundColor: "#FEE2E2", color: C.danger, border: `1px solid ${C.danger}44` }
                  : { backgroundColor: C.white, color: C.navy, border: `1px solid ${C.border}`, borderRadius: "4px 18px 18px 18px" }
              }
            >
              {m.role === "assistant" ? (
                <div className="prose prose-sm max-w-none [&_p]:m-0 [&_ul]:my-1 [&_li]:my-0">
                  <ReactMarkdown rehypePlugins={[rehypeSanitize]}>{m.content}</ReactMarkdown>
                </div>
              ) : m.content}
            </div>
          </div>
        ))}

        {/* Loading */}
        {loading && (
          <div className="flex justify-start gap-2">
            <span className="text-lg shrink-0 mt-0.5">🧠</span>
            <div className="rounded-2xl px-4 py-4 flex-1 max-w-[85%]" style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}>
              <div className="font-semibold text-sm mb-3" style={{ color: C.navy }}>Analyse en cours...</div>
              <div className="relative h-2 rounded-full mb-1 overflow-hidden" style={{ backgroundColor: C.border }}>
                <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${loadingProgress}%`, background: `linear-gradient(90deg, ${C.teal}, #5BBFB5)`, transition: "width 0.5s ease-out" }} />
                <div className="ia-shimmer absolute inset-y-0 w-1/3 rounded-full pointer-events-none" style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)" }} />
              </div>
              <div className="text-right text-[10px] mb-3" style={{ color: C.muted }}>{loadingProgress} %</div>
              <div className="text-xs flex items-center gap-1.5" style={{ color: C.teal }}>
                <span className="w-1.5 h-1.5 rounded-full animate-pulse inline-block" style={{ backgroundColor: C.teal }} />
                {LOADING_MESSAGES[loadingStepIdx]}
              </div>
              <p className="mt-2 text-xs" style={{ color: C.muted }}>⏱ ~1 minute · Ne fermez pas cet onglet</p>
            </div>
          </div>
        )}

        {limitReached && (
          <p className="text-center text-sm font-medium py-3" style={{ color: C.danger }}>
            Limite de 20 messages atteinte — reconnectez-vous pour continuer.
          </p>
        )}
      </div>

      {/* Paywall */}
      {iaBlocked && <TeaserPaywall />}

      {/* Input + RGPD inline */}
      {!iaBlocked && (
        <div className="mt-3 space-y-2">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage(input)}
              placeholder={limitReached ? "Limite atteinte" : "Posez votre question fiscale..."}
              disabled={loading || limitReached}
              className="flex-1 rounded-xl px-4 py-3 text-sm border outline-none focus:ring-2 disabled:opacity-50"
              style={{ borderColor: C.border, color: C.navy }}
            />
            <button
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || loading || limitReached}
              className="w-12 h-12 rounded-xl flex items-center justify-center text-white transition-opacity disabled:opacity-40 shrink-0"
              style={{ backgroundColor: C.teal }}
              aria-label="Envoyer"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
          <p className="text-[11px] text-center" style={{ color: C.muted }}>
            🔒 N'entrez jamais le nom de vos patients · Utilisez des pseudonymes · Taux CIPAV 2026
          </p>
        </div>
      )}
    </div>
  );
}
